import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Download, Wallet } from 'lucide-react';

const InvoiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentData, setPaymentData] = useState({
    amount: 0,
    mode: 'CASH',
    date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  const businessId = localStorage.getItem('currentBusiness');

  useEffect(() => {
    fetchInvoice();
  }, [id]);

  const fetchInvoice = async () => {
    try {
      console.log('Fetching invoice:', businessId, id);
      const response = await api.get(`/api/invoices/${businessId}/${id}`);
      console.log('Invoice response:', response.data);
      setInvoice(response.data.invoice);
      setPaymentData({ ...paymentData, amount: response.data.invoice.total - response.data.invoice.receivedAmount });
    } catch (error) {
      console.error('Failed to fetch invoice:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.error || error.response?.data?.detail || 'Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await api.post(`/api/invoices/${businessId}/${id}/pdf`, {}, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${invoice.invoiceNo}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/payments', {
        businessId,
        invoiceId: id,
        customerId: invoice.customerId,
        ...paymentData
      });
      toast.success('Payment recorded successfully');
      setShowPaymentDialog(false);
      fetchInvoice();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to record payment');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!invoice) {
    return <div className="text-center py-12">Invoice not found</div>;
  }

  const outstanding = invoice.total - invoice.receivedAmount;

  return (
    <div className="space-y-6 max-w-5xl" data-testid="invoice-detail-page">
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/invoices')} data-testid="back-to-invoices-button">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold font-heading font-mono">{invoice.invoiceNo}</h1>
            <p className="text-sm text-gray-600 mt-1">
              {new Date(invoice.invoiceDate).toLocaleDateString('en-IN')}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleDownloadPDF} variant="outline" data-testid="download-pdf-button">
            <Download className="w-4 h-4 mr-2" />
            Download PDF
          </Button>
          {outstanding > 0 && (
            <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
              <DialogTrigger asChild>
                <Button className="bg-green-600 hover:bg-green-700" data-testid="record-payment-button">
                  <Wallet className="w-4 h-4 mr-2" />
                  Record Payment
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Record Payment</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleRecordPayment} className="space-y-4" data-testid="payment-form">
                  <div className="space-y-2">
                    <Label htmlFor="amount">Amount *</Label>
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      value={paymentData.amount}
                      onChange={(e) => setPaymentData({ ...paymentData, amount: parseFloat(e.target.value) })}
                      required
                      data-testid="payment-amount-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="mode">Payment Mode *</Label>
                    <select
                      id="mode"
                      value={paymentData.mode}
                      onChange={(e) => setPaymentData({ ...paymentData, mode: e.target.value })}
                      className="w-full h-10 px-3 rounded-md border border-input bg-background"
                      required
                      data-testid="payment-mode-select"
                    >
                      <option value="CASH">Cash</option>
                      <option value="UPI">UPI</option>
                      <option value="BANK_TRANSFER">Bank Transfer</option>
                      <option value="CARD">Card</option>
                      <option value="CHEQUE">Cheque</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="date">Payment Date *</Label>
                    <Input
                      id="date"
                      type="date"
                      value={paymentData.date}
                      onChange={(e) => setPaymentData({ ...paymentData, date: e.target.value })}
                      required
                      data-testid="payment-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="notes">Notes</Label>
                    <Input
                      id="notes"
                      value={paymentData.notes}
                      onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                      placeholder="Payment reference or notes"
                      data-testid="payment-notes-input"
                    />
                  </div>
                  <Button type="submit" className="w-full" data-testid="submit-payment-button">
                    Record Payment
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle>Invoice Details</CardTitle>
                <p className="text-sm text-gray-600 mt-1">{invoice.type.replace('_', ' ')}</p>
              </div>
              <Badge className={invoice.status === 'PAID' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}>
                {invoice.status.replace('_', ' ')}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h3 className="font-semibold mb-2">Bill To:</h3>
              <div className="text-sm text-gray-600">
                <div className="font-medium text-gray-900">{invoice.customer.name}</div>
                {invoice.customer.billingAddress && <div>{invoice.customer.billingAddress}</div>}
                {invoice.customer.phone && <div>Phone: {invoice.customer.phone}</div>}
                {invoice.customer.gstin && <div>GSTIN: {invoice.customer.gstin}</div>}
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-3">Items:</h3>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-3">Item</th>
                      <th className="text-right p-3">Qty</th>
                      <th className="text-right p-3">Rate</th>
                      <th className="text-right p-3">GST%</th>
                      <th className="text-right p-3">CGST</th>
                      <th className="text-right p-3">SGST</th>
                      <th className="text-right p-3">IGST</th>
                      <th className="text-right p-3">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.lines.map((line, index) => (
                      <tr key={index} className="border-t">
                        <td className="p-3">
                          <div>{line.name}</div>
                          {line.hsn && <div className="text-xs text-gray-500">HSN: {line.hsn}</div>}
                        </td>
                        <td className="text-right p-3">{line.qty}</td>
                        <td className="text-right p-3">₹{line.rate.toFixed(2)}</td>
                        <td className="text-right p-3">{line.gstPercent}%</td>
                        <td className="text-right p-3 text-xs">₹{(line.cgst || 0).toFixed(2)}</td>
                        <td className="text-right p-3 text-xs">₹{(line.sgst || 0).toFixed(2)}</td>
                        <td className="text-right p-3 text-xs">₹{(line.igst || 0).toFixed(2)}</td>
                        <td className="text-right p-3 font-semibold">₹{line.amount.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {invoice.notes && (
              <div>
                <h3 className="font-semibold mb-2">Notes:</h3>
                <p className="text-sm text-gray-600">{invoice.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Subtotal:</span>
                <span className="font-semibold">₹{invoice.subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Tax Amount:</span>
                <span className="font-semibold">₹{invoice.taxAmount.toFixed(2)}</span>
              </div>
              {invoice.discount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Discount:</span>
                  <span className="font-semibold">-₹{invoice.discount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold border-t pt-3">
                <span>Total:</span>
                <span className="text-teal-700">₹{invoice.total.toFixed(2)}</span>
              </div>
              {invoice.receivedAmount > 0 && (
                <>
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Paid:</span>
                    <span className="font-semibold">₹{invoice.receivedAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold text-orange-600">
                    <span>Outstanding:</span>
                    <span>₹{outstanding.toFixed(2)}</span>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {invoice.payments && invoice.payments.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Payment History</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {invoice.payments.map((payment) => (
                    <div key={payment.id} className="flex justify-between text-sm border-b pb-2">
                      <div>
                        <div className="font-medium">{payment.mode}</div>
                        <div className="text-xs text-gray-500">
                          {new Date(payment.date).toLocaleDateString('en-IN')}
                        </div>
                      </div>
                      <div className="font-semibold text-green-600">₹{payment.amount.toFixed(2)}</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvoiceDetail;